import React, { useState } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { DollarSign, Download, TrendingUp, Calendar, CreditCard, ArrowUpRight, ArrowDownRight, Music2 } from 'lucide-react';

const Royalties = () => {
  const [timeframe, setTimeframe] = useState('7d');

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Royalties</h1>
            <p className="text-gray-600">Track your earnings and payment history</p>
          </div>
          <div className="flex items-center gap-4">
            <select
              value={timeframe}
              onChange={(e) => setTimeframe(e.target.value)}
              className="bg-white border border-gray-200 rounded-xl px-4 py-2 text-gray-700 focus:outline-none focus:border-purple-500 transition-colors"
            >
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
              <option value="90d">Last 90 days</option>
              <option value="1y">Last year</option>
            </select>
            <button className="flex items-center gap-2 bg-purple-50 hover:bg-purple-100 text-purple-600 px-4 py-2 rounded-xl transition-colors font-medium">
              <Download className="h-5 w-5" />
              Export
            </button>
          </div>
        </div>

        {/* Overview Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <StatCard
            title="Total Earnings"
            value="$0.00"
            change="0%"
            isPositive={true}
            icon={<DollarSign className="h-5 w-5" />}
          />
          <StatCard
            title="Monthly Revenue"
            value="$0.00"
            change="0%"
            isPositive={true}
            icon={<TrendingUp className="h-5 w-5" />}
          />
          <StatCard
            title="Pending Payments"
            value="$0.00"
            change="0%"
            isPositive={true}
            icon={<Calendar className="h-5 w-5" />}
          />
          <StatCard
            title="Next Payout"
            value="$0.00"
            date="N/A"
            icon={<CreditCard className="h-5 w-5" />}
          />
        </div>

        {/* Revenue Chart */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-xl shadow-purple-100/20 mb-8">
          <h3 className="text-xl font-bold text-gray-900 mb-6">Revenue Overview</h3>
          <div className="h-64 flex items-end justify-between gap-4 px-4">
            <Bar height={0} label="Jan" value="$0" />
            <Bar height={0} label="Feb" value="$0" />
            <Bar height={0} label="Mar" value="$0" />
            <Bar height={0} label="Apr" value="$0" />
            <Bar height={0} label="May" value="$0" />
            <Bar height={0} label="Jun" value="$0" />
          </div>
        </div>

        {/* Detailed Breakdown */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-xl shadow-purple-100/20">
            <h3 className="text-xl font-bold text-gray-900 mb-6">Revenue by Platform</h3>
            <div className="space-y-4">
              <PlatformRow
                platform="No data yet"
                amount="$0.00"
                percentage="0%"
                change="0%"
                isPositive={true}
              />
            </div>
          </div>
          <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-xl shadow-purple-100/20">
            <h3 className="text-xl font-bold text-gray-900 mb-6">Top Earning Tracks</h3>
            <div className="space-y-4">
              <TrackRow
                title="No tracks yet"
                amount="$0.00"
                streams="0"
                change="0%"
                isPositive={true}
              />
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

const StatCard = ({ title, value, change, isPositive, date, icon }: { title: string; value: string; change?: string; isPositive?: boolean; date?: string; icon: React.ReactNode }) => (
  <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-xl shadow-purple-100/20">
    <div className="flex items-center justify-between mb-4">
      <div className="bg-purple-50 p-2 rounded-xl">
        {React.cloneElement(icon as React.ReactElement, {
          className: 'text-purple-600'
        })}
      </div>
      {change && (
        <div className={`flex items-center ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
          {isPositive ? <ArrowUpRight className="h-4 w-4" /> : <ArrowDownRight className="h-4 w-4" />}
          <span className="text-sm font-medium">{change}</span>
        </div>
      )}
      {date && (
        <div className="text-sm text-gray-600 font-medium">
          {date}
        </div>
      )}
    </div>
    <h3 className="text-2xl font-bold text-gray-900 mb-1">{value}</h3>
    <p className="text-gray-600 text-sm">{title}</p>
  </div>
);

const Bar = ({ height, label, value }: { height: number; label: string; value: string }) => (
  <div className="flex flex-col items-center gap-2">
    <div className="relative w-12">
      <div
        className="w-full bg-purple-100 rounded-t-lg absolute bottom-0"
        style={{ height: `${height}%` }}
      >
        <div
          className="w-full bg-purple-500 rounded-t-lg absolute bottom-0 opacity-50"
          style={{ height: `${height}%` }}
        />
      </div>
    </div>
    <span className="text-sm text-gray-600">{label}</span>
    <span className="text-sm font-medium text-gray-900">{value}</span>
  </div>
);

const PlatformRow = ({ platform, amount, percentage, change, isPositive }: { platform: string; amount: string; percentage: string; change: string; isPositive: boolean }) => (
  <div className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0">
    <div className="flex items-center gap-3">
      <div className="bg-purple-50 p-2 rounded-xl">
        <DollarSign className="h-4 w-4 text-purple-600" />
      </div>
      <div>
        <div className="font-medium text-gray-900">{platform}</div>
        <div className="text-sm text-gray-600">{percentage} of total</div>
      </div>
    </div>
    <div className="flex items-center gap-4">
      <span className="font-medium text-gray-900">{amount}</span>
      <span className={isPositive ? 'text-green-600' : 'text-red-600'}>
        {change}
      </span>
    </div>
  </div>
);

const TrackRow = ({ title, amount, streams, change, isPositive }: { title: string; amount: string; streams: string; change: string; isPositive: boolean }) => (
  <div className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0">
    <div className="flex items-center gap-3">
      <div className="bg-purple-50 p-2 rounded-xl">
        <Music2 className="h-4 w-4 text-purple-600" />
      </div>
      <div>
        <div className="font-medium text-gray-900">{title}</div>
        <div className="text-sm text-gray-600">{streams} streams</div>
      </div>
    </div>
    <div className="flex items-center gap-4">
      <span className="font-medium text-gray-900">{amount}</span>
      <span className={isPositive ? 'text-green-600' : 'text-red-600'}>
        {change}
      </span>
    </div>
  </div>
);

export default Royalties;